import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ImageCarousel } from "@/components/ImageCarousel";
import { trackEvent } from "@/lib/analytics";
import { ShoppingBag, Check, Instagram, CreditCard, FileText, Send, Star } from "lucide-react";
import api from "@/lib/api";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function PackDetailDrawer({ pack, open, onClose, apiUrl }) {
  const navigate = useNavigate();
  const [packReviews, setPackReviews] = useState([]);

  useEffect(() => {
    if (pack?._id && open) {
      api.get(`/reviews?pack_id=${pack._id}`).then(({ data }) => setPackReviews(data || [])).catch(() => setPackReviews([]));
    } else {
      setPackReviews([]);
    }
  }, [pack?._id, open]);

  if (!pack) return null;

  const effectivePrice = pack.discounted_price ?? pack.price;
  const hasPdf = !!pack.pdf_path;
  const canBuy = pack.status === "published" && hasPdf;

  function handleBuyNow() {
    trackEvent("buy_click", pack._id);
    onClose();
    navigate(`/checkout/${pack._id}`);
  }

  return (
    <Drawer open={open} onOpenChange={(o) => !o && onClose()}>
      <DrawerContent
        className="max-h-[92vh] rounded-t-3xl"
        data-testid="pack-detail-sheet"
      >
        <div className="overflow-y-auto pb-8">
          <DrawerHeader className="p-0">
            <div className="rounded-t-3xl overflow-hidden">
              <ImageCarousel images={pack.images} apiUrl={apiUrl} large />
            </div>
            <div className="px-5 pt-4 space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className="text-xs bg-coral/10 text-coral border-0 rounded-full">
                  {pack.category}
                </Badge>
                {pack.age_range && (
                  <Badge variant="outline" className="text-xs rounded-full">
                    {pack.age_range}
                  </Badge>
                )}
                {pack.page_count && (
                  <Badge variant="outline" className="text-xs rounded-full">
                    {pack.page_count} pages
                  </Badge>
                )}
                {pack.status === "coming_soon" && (
                  <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-xs rounded-full">
                    Coming Soon
                  </Badge>
                )}
              </div>
              <DrawerTitle className="font-heading text-2xl text-left">
                {pack.title}
              </DrawerTitle>
              {pack.description && (
                <DrawerDescription className="text-base text-slate-600 text-left">
                  {pack.description}
                </DrawerDescription>
              )}
            </div>
          </DrawerHeader>

          <div className="px-5 mt-4 space-y-4">
            {/* Price */}
            <div className="bg-secondary rounded-2xl p-4">
              {pack.discount_label && (
                <span className="inline-block text-xs font-bold text-green-700 bg-green-100 rounded-full px-2.5 py-0.5 mb-2">
                  {pack.discount_label}
                </span>
              )}
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500 font-medium">Price</span>
                {pack.discounted_price != null && pack.discounted_price !== "" ? (
                  <div className="flex items-baseline gap-2">
                    <span className="font-heading font-bold text-2xl text-coral">₹{pack.discounted_price}</span>
                    <span className="font-heading font-semibold text-base text-slate-400 line-through">₹{pack.price}</span>
                  </div>
                ) : (
                  <span className="font-heading font-bold text-2xl text-slate-800">₹{pack.price}</span>
                )}
              </div>
            </div>

            {/* How it works */}
            <div className="bg-blue-50/70 rounded-2xl p-4 space-y-3">
              <h4 className="font-heading font-semibold text-slate-700 text-base">How it works</h4>
              <div className="space-y-2.5">
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full bg-coral/10 flex items-center justify-center shrink-0 mt-0.5">
                    <CreditCard className="w-3.5 h-3.5 text-coral" />
                  </div>
                  <p className="text-sm text-slate-600 font-body">Pay securely via UPI, Card, or Netbanking</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full bg-coral/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3.5 h-3.5 text-coral" />
                  </div>
                  <p className="text-sm text-slate-600 font-body">Get instant download + email with your PDF</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full bg-coral/10 flex items-center justify-center shrink-0 mt-0.5">
                    <FileText className="w-3.5 h-3.5 text-coral" />
                  </div>
                  <p className="text-sm text-slate-600 font-body">Print at home on A4 — unlimited prints!</p>
                </div>
              </div>
            </div>

            {/* Buy CTA */}
            {canBuy && (
              <Button
                onClick={handleBuyNow}
                className="w-full bg-coral hover:bg-coral-hover text-white rounded-full text-lg h-14 font-bold shadow-md shadow-coral/20 active:scale-[0.98] transition-transform"
                data-testid="buy-now-btn"
              >
                <ShoppingBag className="w-5 h-5 mr-2" />
                Buy Now — ₹{effectivePrice}
              </Button>
            )}

            {pack.status === "published" && !hasPdf && (
              <div className="text-center py-2 text-sm text-slate-400">Available soon</div>
            )}

            {pack.status === "coming_soon" && (
              <div className="text-center py-3">
                <p className="text-amber-700 font-semibold text-sm">This pack is coming soon!</p>
                <a href="https://instagram.com/playawayclub" target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-coral font-bold text-sm mt-2">
                  <Instagram className="w-4 h-4" /> @playawayclub
                </a>
              </div>
            )}

            {/* Pack Reviews */}
            {packReviews.length > 0 && (
              <div className="space-y-3 pt-2">
                <h4 className="font-heading font-semibold text-slate-700 text-base">What parents say</h4>
                {packReviews.map((r) => (
                  <div key={r._id} className="bg-secondary/50 rounded-xl p-3 space-y-2">
                    <div className="flex gap-0.5">
                      {[1,2,3,4,5].map((s) => (
                        <Star key={s} className={`w-3 h-3 ${s <= r.rating ? "text-amber-400 fill-amber-400" : "text-slate-200"}`} />
                      ))}
                    </div>
                    <p className="text-sm text-slate-700 font-body">"{r.text}"</p>
                    <p className="text-[10px] text-slate-400">
                      — {r.parent_name}{r.city ? `, ${r.city}` : ""}{r.child_age ? ` · child age ${r.child_age}` : ""}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
